import { DeleteOutlineRounded, DriveFileRenameOutline, HistoryOutlined, MoreTimeRounded } from "@mui/icons-material";
import { Avatar, Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import moment from "moment";

import { getDisplayName, getInitials } from "../../utils/user.util";
import { IconButton } from "../v3";

const TASK_STATUS_CONFIG = {
  DONE: {
    label: "Done",
    color: "success",
  },
  INPROGRESS: {
    label: "In Progress",
    color: "warning",
  },
  TODO: {
    label: "Todo",
    color: "primary",
  },
};

const getTimingLabel = (task: any) => {
  const now = moment();
  const due = task.dueDate ? moment(task.dueDate) : null;
  const start = task.startDate ? moment(task.startDate) : null;
  const end = task.endDate ? moment(task.endDate) : null;

  const formatDuration = (from: moment.Moment, to: moment.Moment): string => {
    const totalMinutes = to.diff(from, "minutes");
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;
    const formatUnit = (value: number, unit: string): string => `${value} ${unit}${value === 1 ? "" : "s"}`;

    if (days > 0 && hours > 0) return `${formatUnit(days, "day")} ${formatUnit(hours, "hour")}`;
    if (days > 0 && minutes > 0) return `${formatUnit(days, "day")} ${formatUnit(minutes, "minute")}`;
    if (days > 0) return formatUnit(days, "day");
    if (hours > 0 && minutes > 0) return `${formatUnit(hours, "hour")} ${formatUnit(minutes, "minute")}`;
    if (hours > 0) return formatUnit(hours, "hour");
    return formatUnit(minutes, "minute");
  };

  if (task.status === "DONE" && start && end) {
    return { label: `${formatDuration(start, end)}`, color: "text.secondary", isOverdue: false };
  }

  if (task.status === "INPROGRESS" && due && now.isAfter(due, "day")) {
    const overdueFrom = moment(due).endOf("day");
    return {
      label: `Overdue ${formatDuration(overdueFrom, now)}`,
      color: "error.main",
      isOverdue: true,
    };
  }

  if (task.status === "INPROGRESS" && start) {
    return { label: `${formatDuration(start, now)}`, color: "text.secondary", isOverdue: false };
  }

  if (task.status === "TODO" && due && now.isAfter(due, "day")) {
    return { label: `Overdue ${formatDuration(due.endOf("day"), now)}`, color: "error.main", isOverdue: true };
  }

  if (due) {
    return { label: due.format("DD MMM YYYY"), color: "text.secondary", isOverdue: false };
  }

  return null;
};

export const TaskCard = ({ task, canUpdateProgress, canEdit, canDelete, onUpdateProgress, onViewProgress, onEdit, onDelete }) => {
  const status = TASK_STATUS_CONFIG[task.status] || TASK_STATUS_CONFIG.TODO;
  const timing = getTimingLabel(task);

  return (
    <Box
      sx={{
        p: "10px 12px",
        borderRadius: "10px",
        border: "none",
        bgcolor: "#F6F6F6",
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {/* Top Row: title + status chip + action buttons */}
      <Stack direction="row" alignItems="center" spacing={0.75}>
        {/* Status chip */}
        <Chip
          label={status.label}
          size="small"
          color={status.color}
          variant="filled"
          sx={{ fontWeight: 500, height: 18, fontSize: 10, flexShrink: 0 }}
        />

        {/* Title — truncated to 1 line */}
        <Tooltip title={task.title} placement="top" enterDelay={600}>
          <Typography
            fontWeight={500}
            fontSize={13}
            sx={{
              flex: 1,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              lineHeight: 1.4,
            }}
          >
            {task.title}
          </Typography>
        </Tooltip>

        {/* Action buttons */}
        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
          {canUpdateProgress && (
            <Tooltip title="Update progress" placement="top">
              <span>
                <IconButton
                  icon={<MoreTimeRounded sx={{ fontSize: 14 }} />}
                  onClick={() => onUpdateProgress(task)}
                  styles={{
                    backgroundColor: "transparent",
                    borderRadius: "4px",
                    color: "#674BF5",
                    padding: "2px",
                    minWidth: "unset",
                    width: 22,
                    height: 22,
                  }}
                  hoverStyles={{ backgroundColor: "#EDE9FD", color: "#674BF5" }}
                />
              </span>
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip title="Edit" placement="top">
              <span>
                <IconButton
                  icon={<DriveFileRenameOutline sx={{ fontSize: 14 }} />}
                  onClick={() => onEdit(task)}
                  styles={{
                    backgroundColor: "transparent",
                    borderRadius: "4px",
                    color: "#4b5d78ff",
                    padding: "2px",
                    minWidth: "unset",
                    width: 22,
                    height: 22,
                  }}
                  hoverStyles={{ backgroundColor: "#EFEFEF", color: "#333" }}
                />
              </span>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Delete" placement="top">
              <span>
                <IconButton
                  icon={<DeleteOutlineRounded sx={{ fontSize: 14 }} />}
                  onClick={() => onDelete(task)}
                  styles={{
                    backgroundColor: "transparent",
                    borderRadius: "4px",
                    color: "#c78484ff",
                    padding: "2px",
                    minWidth: "unset",
                    width: 22,
                    height: 22,
                  }}
                  hoverStyles={{ backgroundColor: "#FEECEC", color: "#D32F2F" }}
                />
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {/* Bottom Row: assignee + timing + attachment/notes icons */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        {/* Left: assignee + timing */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          {task.assignee && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
              <Avatar sx={{ width: 18, height: 18, fontSize: 9 }}>{getInitials(task.assignee)}</Avatar>
              <Typography
                variant="caption"
                sx={{
                  fontSize: 11,
                  color: "text.secondary",
                  maxWidth: 150,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {getDisplayName(task.assignee)}
              </Typography>
            </Stack>
          )}

          {task.assignee && timing && (
            <Box sx={{ width: "3px", height: "3px", borderRadius: "50%", bgcolor: "grey.400", flexShrink: 0 }} />
          )}

          {timing && (
            <Typography
              variant="caption"
              fontWeight={timing.isOverdue ? 600 : 400}
              sx={{ fontSize: 11, whiteSpace: "nowrap", color: timing.color }}
            >
              {timing.label}
            </Typography>
          )}
        </Stack>

        {/* Right: progress count */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          {(() => {
            const hasProgress = (task.progressCount || 0) > 0;
            return (
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                onClick={() => hasProgress && onViewProgress?.(task)}
                sx={{
                  cursor: hasProgress ? "pointer" : "default",
                  opacity: hasProgress ? 1 : 0.5,
                  "&:hover": hasProgress ? { opacity: 0.8 } : {},
                }}
              >
                <Typography variant="caption" sx={{ fontSize: 11, color: "text.secondary" }}>
                  Progress
                </Typography>
                <HistoryOutlined
                  sx={{
                    fontSize: 18,
                    color: hasProgress ? "#4CAF50" : "text.disabled",
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 11,
                    color: hasProgress ? "#4CAF50" : "text.disabled",
                    fontWeight: hasProgress ? 600 : 400,
                  }}
                >
                  {task.progressCount || 0}
                </Typography>
              </Stack>
            );
          })()}
        </Stack>
      </Stack>
    </Box>
  );
};
